import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

type Position = {
	top: number;
	left: number;
	width: number;
	openUp: boolean;
};

export const usePaymentStatusDropdown = () => {
	const [opened, setOpened] = useState(false);
	const targetRef = useRef<HTMLDivElement | null>(null);
	const dropdownRef = useRef<HTMLDivElement | null>(null);
	const [dropdownPosition, setDropdownPosition] = useState<Position>({
		top: 0,
		left: 0,
		width: 180,
		openUp: false,
	});

	const updateDropdownPosition = useCallback(() => {
		if (!targetRef.current) {
			return;
		}

		const rect = targetRef.current.getBoundingClientRect();
		const dropdownHeight = 120;
		const viewportPadding = 8;
		const shouldOpenUp =
			rect.bottom + 6 + dropdownHeight > window.innerHeight - viewportPadding &&
			rect.top - 6 - dropdownHeight > viewportPadding;

		setDropdownPosition({
			top: shouldOpenUp ? rect.top - 6 : rect.bottom + 6,
			left: rect.left,
			width: Math.max(rect.width, 180),
			openUp: shouldOpenUp,
		});
	}, []);

	useLayoutEffect(() => {
		if (!opened) {
			return;
		}

		updateDropdownPosition();
	}, [opened, updateDropdownPosition]);

	useEffect(() => {
		if (!opened) {
			return;
		}

		const handlePointerDown = (event: MouseEvent) => {
			const target = event.target as Node;
			if (targetRef.current?.contains(target) || dropdownRef.current?.contains(target)) {
				return;
			}

			setOpened(false);
		};

		const handleWindowChange = () => {
			updateDropdownPosition();
		};

		document.addEventListener('mousedown', handlePointerDown);
		window.addEventListener('resize', handleWindowChange);
		window.addEventListener('scroll', handleWindowChange, true);

		return () => {
			document.removeEventListener('mousedown', handlePointerDown);
			window.removeEventListener('resize', handleWindowChange);
			window.removeEventListener('scroll', handleWindowChange, true);
		};
	}, [opened, updateDropdownPosition]);

	const toggleDropdown = () => {
		setOpened((prev) => {
			return !prev;
		});
	};

	const closeDropdown = () => {
		setOpened(false);
	};

	return {
		opened,
		targetRef,
		dropdownRef,
		dropdownPosition,
		toggleDropdown,
		closeDropdown,
	};
};
